import * as LabelPrimitive from "@radix-ui/react-label";
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { useId } from "react";

import { cn } from "@/lib/utils";

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
  /** The hint under the input, so inputs side by side line up (M51). */
  hintBelow?: boolean;
}

/**
 * A labelled input with its hint and error wired up for screen readers
 * (aria-describedby, aria-invalid), so no form repeats that work.
 */
export function Field({ label, hint, error, className, id, hintBelow, ...props }: FieldProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={inputId} className="text-sm font-medium">
        {label}
      </LabelPrimitive.Root>
      {hint && !hintBelow ? <Hint id={hintId}>{hint}</Hint> : null}
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          "min-h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm placeholder:text-muted-foreground",
          error && "border-error",
          className,
        )}
        {...props}
      />
      {hint && hintBelow ? <Hint id={hintId}>{hint}</Hint> : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}

function Hint({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs text-muted-foreground">
      {children}
    </p>
  );
}

export function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs font-medium text-error">
      {children}
    </p>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hint?: string;
  error?: string | undefined;
  /** The hint under the select, as `Field`'s. */
  hintBelow?: boolean;
}

/** A labelled select, wired up like `Field`. */
export function SelectField({
  label,
  hint,
  error,
  className,
  id,
  children,
  hintBelow,
  ...props
}: SelectFieldProps) {
  const generated = useId();
  const selectId = id ?? generated;
  const hintId = `${selectId}-hint`;
  const errorId = `${selectId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={selectId} className="text-sm font-medium">
        {label}
      </LabelPrimitive.Root>
      {hint && !hintBelow ? <Hint id={hintId}>{hint}</Hint> : null}
      <select
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          "min-h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm placeholder:text-muted-foreground",
          error && "border-error",
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {hint && hintBelow ? <Hint id={hintId}>{hint}</Hint> : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  ref?: Ref<HTMLTextAreaElement>;
  hint?: string;
  error?: string | undefined;
}

/** A labelled multi-line field, wired up like `Field`. */
export function TextAreaField({ label, hint, error, className, id, ...props }: TextAreaFieldProps) {
  const generated = useId();
  const areaId = id ?? generated;
  const hintId = `${areaId}-hint`;
  const errorId = `${areaId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ");
  return (
    <div className="flex flex-col gap-1.5">
      <LabelPrimitive.Root htmlFor={areaId} className="text-sm font-medium">
        {label}
      </LabelPrimitive.Root>
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      <textarea
        id={areaId}
        rows={3}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(
          "min-h-20 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground",
          error && "border-error",
          className,
        )}
        {...props}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </div>
  );
}
